import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAuthEditModal } from "./UserAuthEditModal";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ProfissionalAcessoProps {
  professionalId: string;
  email: string;
}

export function ProfissionalAcesso({ professionalId, email }: ProfissionalAcessoProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("7");

  useEffect(() => {
    loadAccessLogs();
  }, [professionalId, dateFilter]);

  const loadAccessLogs = async () => {
    try {
      setLoading(true);
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(dateFilter));

      const { data, error } = await supabase
        .from("audit_logs")
        .select("*")
        .eq("actor_id", professionalId)
        .gte("created_at", daysAgo.toISOString())
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error("Error loading access logs:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Dados de Acesso</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{email}</p>
            </div>
            <Button onClick={() => setShowAuthModal(true)}>
              Gerenciar Acesso
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Histórico de Acessos</CardTitle>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Este mês</SelectItem>
                <SelectItem value="90">Últimos 3 meses</SelectItem>
                <SelectItem value="365">Este ano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Carregando...</p>
          ) : logs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum acesso registrado</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Dispositivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {format(new Date(log.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>{log.action || "—"}</TableCell>
                    <TableCell>{log.ip || "—"}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {log.user_agent ? log.user_agent.substring(0, 50) + "..." : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {showAuthModal && (
        <UserAuthEditModal
          open={showAuthModal}
          onOpenChange={setShowAuthModal}
          userId={professionalId}
          currentEmail={email}
        />
      )}
    </div>
  );
}